import { useEffect, useMemo, useState } from "react";
import {
  Card,
  Col,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Divider,
  Progress,
  Empty,
} from "antd";
import { useQuery } from "@tanstack/react-query";
import {
  BookOutlined,
  VideoCameraOutlined,
  CloudServerOutlined,
  BankOutlined,
  ShopOutlined,
  StarOutlined,
  BarChartOutlined,
  TrophyOutlined,
  PieChartOutlined,
  UserOutlined,
  GlobalOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { Tv } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { myLibraryApi } from "../services/api";
import type {
  PlatformStatItem,
  PublisherStatItem,
  CinemaStatItem,
  CinemaHallStatItem,
  DirectorStatItem,
  MovieGenreStatItem,
  MovieNationalityStatItem,
  SeriesNationalityStatItem,
  BookPurchaseCostItem,
} from "../services/api";
import { RatingDisplay, RATING_OPTIONS } from "../components/RatingDisplay";

const { Title, Text } = Typography;

const COLORS = {
  primary: "#1677ff",
  books: "#52c41a",
  movies: "#1677ff",
  series: "#faad14",
  total: "#722ed1",
};

const PIE_COLORS = [
  "#1677ff",
  "#52c41a",
  "#faad14",
  "#722ed1",
  "#f5222d",
  "#13c2c2",
  "#eb2f96",
  "#fa8c16",
  "#2f54eb",
  "#a0d911",
  "#597ef7",
  "#36cfc9",
  "#f759ab",
  "#ffc53d",
  "#40a9ff",
];

const RATING_COLORS: Record<number, string> = Object.fromEntries(
  RATING_OPTIONS.map((opt) => [opt.value, opt.color]),
);

const RATING_LABELS: Record<number, string> = Object.fromEntries(
  RATING_OPTIONS.map((opt) => [opt.value, opt.label]),
);

const TYPE_LABELS: Record<string, string> = {
  book: "Książki",
  movie: "Filmy",
  series: "Seriale",
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  book: <BookOutlined style={{ color: COLORS.books }} />,
  movie: <VideoCameraOutlined style={{ color: COLORS.movies }} />,
  series: <Tv size="1em" style={{ color: COLORS.series }} />,
};

const MONTH_NAMES = [
  "",
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

function formatMinutes(totalMinutes: number) {
  const m = Math.max(0, Math.floor(totalMinutes || 0));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h <= 0) return `${mm} min`;
  return `${h} godz ${mm} min`;
}

function formatMoney(value: number) {
  return `${value.toFixed(2)} zł`;
}

export function MyLibraryStatsPage() {
  const currentYear = new Date().getFullYear();

  const { data: yearsData, isLoading: yearsLoading } = useQuery({
    queryKey: ["my-library-years"],
    queryFn: myLibraryApi.listYears,
  });

  const years = yearsData?.years ?? [];

  const [year, setYear] = useState<number | undefined>(currentYear);
  const [month, setMonth] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (years.length > 0 && year !== undefined && !years.includes(year)) {
      setYear(years[0]);
    }
  }, [years, year]);

  const yearOptions = useMemo(() => {
    const set = new Set<number>([...years, currentYear]);
    return [
      { value: undefined as unknown as number, label: "Wszystkie lata" },
      ...Array.from(set)
        .sort((a, b) => b - a)
        .map((y) => ({ value: y, label: String(y) })),
    ];
  }, [years, currentYear]);

  const monthOptions = useMemo(
    () => [
      { value: 0, label: "Cały rok" },
      ...Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: MONTH_NAMES[i + 1],
      })),
    ],
    [],
  );

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["my-library-detailed-stats", year, month],
    queryFn: () => myLibraryApi.getDetailedStats({ year, month }),
  });

  // Prepare data for charts
  const ratingChartData = useMemo(() => {
    if (!stats?.rating_stats?.distribution) return [];
    return Object.entries(stats.rating_stats.distribution)
      .filter(([rating]) => Number(rating) >= 1 && Number(rating) <= 8)
      .map(([rating, count]) => ({
        rating: Number(rating),
        label: RATING_LABELS[Number(rating)] || `${rating}`,
        count: count,
        fill: RATING_COLORS[Number(rating)] || "#8884d8",
      }));
  }, [stats]);

  const genreChartData = useMemo(() => {
    if (!stats?.genre_breakdown?.length) return [];
    return stats.genre_breakdown.slice(0, 10).map((g, i) => ({
      name: g.genre_name,
      value: g.works_count,
      fill: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [stats]);

  const platformChartData = useMemo(() => {
    if (!stats?.series_by_platform?.length) return [];
    return stats.series_by_platform.map((p, i) => ({
      name: p.platform_name,
      seasons: p.seasons_count,
      hours: Math.round(p.total_minutes / 60),
      fill: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [stats]);

  const directorChartData = useMemo(() => {
    if (!stats?.movies_by_director?.length) return [];
    return stats.movies_by_director.map((d, i) => ({
      name: d.director_name,
      movies: d.movies_count,
      hours: Math.round(d.total_minutes / 60),
      fill: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [stats]);

  const movieNationalityChartData = useMemo(() => {
    if (!stats?.movies_by_nationality?.length) return [];
    return stats.movies_by_nationality.map((n, i) => ({
      name: n.nationality_name,
      movies: n.movies_count,
      hours: Math.round(n.total_minutes / 60),
      fill: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [stats]);

  const seriesNationalityChartData = useMemo(() => {
    if (!stats?.series_by_nationality?.length) return [];
    return stats.series_by_nationality.map((n, i) => ({
      name: n.nationality_name,
      seasons: n.seasons_count,
      hours: Math.round(n.total_minutes / 60),
      fill: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [stats]);

  const movieGenreChartData = useMemo(() => {
    if (!stats?.movies_by_genre?.length) return [];
    return stats.movies_by_genre.slice(0, 10).map((g, i) => ({
      name: g.genre_name,
      value: g.movies_count,
      fill: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [stats]);

  const filterTitle = useMemo(() => {
    const parts: string[] = [];
    if (year) parts.push(String(year));
    else parts.push("Wszystkie lata");
    if (month) parts.push(MONTH_NAMES[month]);
    return parts.join(" — ");
  }, [year, month]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div>
          <Title level={2} style={{ margin: 0 }}>
            <BarChartOutlined
              style={{ marginRight: 12, color: COLORS.total }}
            />
            Statystyki
          </Title>
          <Text type="secondary" style={{ marginTop: 4, display: "block" }}>
            Szczegółowe statystyki Twojej biblioteki
          </Text>
        </div>

        <Space>
          <span>Rok:</span>
          <Select
            style={{ width: 160 }}
            loading={yearsLoading}
            value={year}
            onChange={(v) => {
              setYear(v);
              if (v === undefined) setMonth(undefined);
            }}
            options={yearOptions}
            allowClear
            placeholder="Wszystkie"
          />
          {year !== undefined && (
            <>
              <span>Miesiąc:</span>
              <Select
                style={{ width: 160 }}
                value={month ?? 0}
                onChange={(v) => setMonth(v === 0 ? undefined : v)}
                options={monthOptions}
              />
            </>
          )}
        </Space>
      </div>

      {/* ============================================================= */}
      {/* 🎬 FILMY                                                       */}
      {/* ============================================================= */}
      <Title level={3} style={{ marginBottom: 16 }}>
        <VideoCameraOutlined style={{ marginRight: 8, color: COLORS.movies }} />
        Filmy
      </Title>

      {/* MOVIES BY CINEMA */}
      <Card
        title={
          <span>
            <ShopOutlined style={{ marginRight: 8, color: COLORS.movies }} />
            Filmy wg kin
          </span>
        }
        loading={statsLoading}
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        {stats?.movies_by_cinema?.length ? (
          <Table<CinemaStatItem>
            dataSource={stats.movies_by_cinema}
            rowKey="cinema_name"
            pagination={false}
            size="small"
            columns={[
              {
                title: "Kino",
                dataIndex: "cinema_name",
                key: "cinema_name",
                render: (name: string) => (
                  <Tag
                    color="blue"
                    style={{ fontSize: 13, padding: "2px 10px" }}
                  >
                    {name}
                  </Tag>
                ),
              },
              {
                title: "Filmy",
                dataIndex: "movies_count",
                key: "movies_count",
                align: "center" as const,
                render: (v: number) => <strong>{v}</strong>,
              },
              {
                title: "Czas oglądania",
                dataIndex: "total_minutes",
                key: "total_minutes",
                align: "right" as const,
                render: (v: number) => formatMinutes(v),
              },
              {
                title: "Koszt biletów",
                dataIndex: "total_ticket_cost",
                key: "total_ticket_cost",
                align: "right" as const,
                render: (v: number) =>
                  v > 0 ? formatMoney(v) : <Text type="secondary">—</Text>,
              },
            ]}
            summary={(data) => {
              const totMovies = data.reduce((s, r) => s + r.movies_count, 0);
              const totMinutes = data.reduce((s, r) => s + r.total_minutes, 0);
              const totCost = data.reduce((s, r) => s + r.total_ticket_cost, 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <strong>Razem</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    <strong>{totMovies}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <strong>{formatMinutes(totMinutes)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <strong>{totCost > 0 ? formatMoney(totCost) : "—"}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        ) : (
          <Empty description="Brak danych o kinach" />
        )}
      </Card>

      {/* MOVIES BY CINEMA HALL */}
      <Card
        title={
          <span>
            <ShopOutlined style={{ marginRight: 8, color: COLORS.movies }} />
            Filmy wg sal kinowych
          </span>
        }
        loading={statsLoading}
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        {stats?.movies_by_cinema_hall?.length ? (
          <Table<CinemaHallStatItem>
            dataSource={stats.movies_by_cinema_hall}
            rowKey={(r) => `${r.cinema_name}-${r.hall_name}`}
            pagination={false}
            size="small"
            columns={[
              {
                title: "Kino",
                dataIndex: "cinema_name",
                key: "cinema_name",
                render: (name: string) => (
                  <Tag
                    color="blue"
                    style={{ fontSize: 13, padding: "2px 10px" }}
                  >
                    {name}
                  </Tag>
                ),
              },
              {
                title: "Sala",
                dataIndex: "hall_name",
                key: "hall_name",
                render: (name: string) =>
                  name === "—" ? <Text type="secondary">—</Text> : name,
              },
              {
                title: "Filmy",
                dataIndex: "movies_count",
                key: "movies_count",
                align: "center" as const,
                render: (v: number) => <strong>{v}</strong>,
              },
              {
                title: "Czas oglądania",
                dataIndex: "total_minutes",
                key: "total_minutes",
                align: "right" as const,
                render: (v: number) => formatMinutes(v),
              },
              {
                title: "Koszt biletów",
                dataIndex: "total_ticket_cost",
                key: "total_ticket_cost",
                align: "right" as const,
                render: (v: number) =>
                  v > 0 ? formatMoney(v) : <Text type="secondary">—</Text>,
              },
            ]}
            summary={(data) => {
              const totMovies = data.reduce((s, r) => s + r.movies_count, 0);
              const totMinutes = data.reduce((s, r) => s + r.total_minutes, 0);
              const totCost = data.reduce((s, r) => s + r.total_ticket_cost, 0);
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <strong>Razem</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}></Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="center">
                    <strong>{totMovies}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <strong>{formatMinutes(totMinutes)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <strong>{totCost > 0 ? formatMoney(totCost) : "—"}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        ) : (
          <Empty description="Brak danych o salach kinowych" />
        )}
      </Card>

      {/* MOVIES BY DIRECTOR */}
      <Card
        title={
          <span>
            <UserOutlined style={{ marginRight: 8, color: COLORS.movies }} />
            Filmy wg reżyserów
          </span>
        }
        loading={statsLoading}
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        {stats?.movies_by_director?.length ? (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={14}>
              <Table<DirectorStatItem>
                dataSource={stats.movies_by_director}
                rowKey="director_name"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Reżyser",
                    dataIndex: "director_name",
                    key: "director_name",
                    render: (name: string) => (
                      <Tag
                        color="purple"
                        style={{ fontSize: 13, padding: "2px 10px" }}
                      >
                        {name}
                      </Tag>
                    ),
                  },
                  {
                    title: "Filmy",
                    dataIndex: "movies_count",
                    key: "movies_count",
                    align: "center" as const,
                    render: (v: number) => <strong>{v}</strong>,
                  },
                  {
                    title: "Czas oglądania",
                    dataIndex: "total_minutes",
                    key: "total_minutes",
                    align: "right" as const,
                    render: (v: number) => formatMinutes(v),
                  },
                ]}
                summary={(data) => {
                  const totMovies = data.reduce(
                    (s, r) => s + r.movies_count,
                    0,
                  );
                  const totMinutes = data.reduce(
                    (s, r) => s + r.total_minutes,
                    0,
                  );
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <strong>Razem</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center">
                        <strong>{totMovies}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <strong>{formatMinutes(totMinutes)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Col>
            <Col xs={24} lg={10}>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={directorChartData.slice(0, 8)}
                    layout="vertical"
                    margin={{ left: 10, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={120}
                      tick={{ fontSize: 12, fill: "#595959" }}
                    />
                    <Tooltip formatter={(value) => [`${value} filmów`, ""]} />
                    <Bar
                      dataKey="movies"
                      fill={COLORS.movies}
                      radius={[0, 4, 4, 0]}
                      barSize={20}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>
        ) : (
          <Empty description="Brak danych o reżyserach" />
        )}
      </Card>

      {/* MOVIES BY GENRE */}
      <Card
        title={
          <span>
            <PieChartOutlined
              style={{ marginRight: 8, color: COLORS.movies }}
            />
            Filmy wg gatunków
          </span>
        }
        loading={statsLoading}
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        {movieGenreChartData.length ? (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <div style={{ width: "100%", height: 340 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={movieGenreChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      innerRadius={50}
                      label={({ name, value }) => `${name} (${value})`}
                      labelLine={true}
                    >
                      {movieGenreChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <Table<MovieGenreStatItem>
                dataSource={stats?.movies_by_genre}
                rowKey="genre_name"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Gatunek",
                    dataIndex: "genre_name",
                    key: "genre_name",
                    render: (name: string) => (
                      <Tag
                        color="cyan"
                        style={{ fontSize: 13, padding: "2px 10px" }}
                      >
                        {name}
                      </Tag>
                    ),
                  },
                  {
                    title: "Filmy",
                    dataIndex: "movies_count",
                    key: "movies_count",
                    align: "center" as const,
                    render: (v: number) => <strong>{v}</strong>,
                  },
                ]}
              />
              <Text
                type="secondary"
                style={{ fontSize: 12, marginTop: 8, display: "block" }}
              >
                * Film może należeć do kilku gatunków — wartości mogą się
                nakładać.
              </Text>
            </Col>
          </Row>
        ) : (
          <Empty description="Brak danych o gatunkach filmów" />
        )}
      </Card>

      {/* MOVIES BY NATIONALITY */}
      <Card
        title={
          <span>
            <GlobalOutlined style={{ marginRight: 8, color: COLORS.movies }} />
            Filmy wg krajów produkcji
          </span>
        }
        loading={statsLoading}
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        {stats?.movies_by_nationality?.length ? (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={14}>
              <Table<MovieNationalityStatItem>
                dataSource={stats.movies_by_nationality}
                rowKey="nationality_name"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Kraj",
                    dataIndex: "nationality_name",
                    key: "nationality_name",
                    render: (name: string, record: MovieNationalityStatItem) =>
                      name === "Brak kraju" ? (
                        <Text type="secondary">—</Text>
                      ) : (
                        <Space>
                          {record.nationality_flag_url && (
                            <img
                              src={record.nationality_flag_url}
                              alt={name}
                              style={{
                                width: 20,
                                height: 14,
                                borderRadius: 2,
                                display: "block",
                                objectFit: "cover",
                              }}
                            />
                          )}
                          {name}
                        </Space>
                      ),
                  },
                  {
                    title: "Filmy",
                    dataIndex: "movies_count",
                    key: "movies_count",
                    align: "center" as const,
                    render: (v: number) => <strong>{v}</strong>,
                  },
                  {
                    title: "Czas oglądania",
                    dataIndex: "total_minutes",
                    key: "total_minutes",
                    align: "right" as const,
                    render: (v: number) => formatMinutes(v),
                  },
                ]}
                summary={(data) => {
                  const totMovies = data.reduce(
                    (s, r) => s + r.movies_count,
                    0,
                  );
                  const totMinutes = data.reduce(
                    (s, r) => s + r.total_minutes,
                    0,
                  );
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <strong>Razem</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center">
                        <strong>{totMovies}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <strong>{formatMinutes(totMinutes)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Col>
            <Col xs={24} lg={10}>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={movieNationalityChartData.slice(0, 8)}
                    layout="vertical"
                    margin={{ left: 10, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={120}
                      tick={{ fontSize: 12, fill: "#595959" }}
                    />
                    <Tooltip formatter={(value) => [`${value} filmów`, ""]} />
                    <Bar
                      dataKey="movies"
                      fill={COLORS.movies}
                      radius={[0, 4, 4, 0]}
                      barSize={20}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>
        ) : (
          <Empty description="Brak danych o krajach produkcji (filmy)" />
        )}
      </Card>

      <Divider style={{ margin: "32px 0" }} />

      {/* ============================================================= */}
      {/* 📺 SERIALE                                                     */}
      {/* ============================================================= */}
      <Title level={3} style={{ marginBottom: 16 }}>
        <Tv size="1em" style={{ marginRight: 8, color: COLORS.series }} />
        Seriale
      </Title>

      {/* SERIES BY PLATFORM */}
      <Card
        title={
          <span>
            <CloudServerOutlined
              style={{ marginRight: 8, color: COLORS.series }}
            />
            Seriale / sezony wg platform streamingowych
          </span>
        }
        loading={statsLoading}
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        {stats?.series_by_platform?.length ? (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={14}>
              <Table<PlatformStatItem>
                dataSource={stats.series_by_platform}
                rowKey="platform_name"
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="small"
                columns={[
                  {
                    title: "Platforma",
                    dataIndex: "platform_name",
                    key: "platform_name",
                    render: (name: string) => (
                      <Tag
                        color="blue"
                        style={{ fontSize: 13, padding: "2px 10px" }}
                      >
                        {name}
                      </Tag>
                    ),
                  },
                  {
                    title: "Seriale",
                    dataIndex: "series_count",
                    key: "series_count",
                    align: "center" as const,
                    render: (v: number) => <strong>{v}</strong>,
                  },
                  {
                    title: "Sezony",
                    dataIndex: "seasons_count",
                    key: "seasons_count",
                    align: "center" as const,
                    render: (v: number) => <strong>{v}</strong>,
                  },
                  {
                    title: "Czas oglądania",
                    dataIndex: "total_minutes",
                    key: "total_minutes",
                    align: "right" as const,
                    render: (v: number) => formatMinutes(v),
                  },
                ]}
                summary={(data) => {
                  const totSeries = data.reduce(
                    (s, r) => s + r.series_count,
                    0,
                  );
                  const totSeasons = data.reduce(
                    (s, r) => s + r.seasons_count,
                    0,
                  );
                  const totMinutes = data.reduce(
                    (s, r) => s + r.total_minutes,
                    0,
                  );
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <strong>Razem</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center">
                        <strong>{totSeries}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="center">
                        <strong>{totSeasons}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <strong>{formatMinutes(totMinutes)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Col>
            <Col xs={24} lg={10}>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={platformChartData}
                      dataKey="seasons"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, value }) => `${name} (${value})`}
                      labelLine={true}
                    >
                      {platformChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} sezonów`, ""]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>
        ) : (
          <Empty description="Brak danych o serialach" />
        )}
      </Card>

      {/* SERIES BY NATIONALITY */}
      <Card
        title={
          <span>
            <GlobalOutlined style={{ marginRight: 8, color: COLORS.series }} />
            Seriale wg krajów produkcji
          </span>
        }
        loading={statsLoading}
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        {stats?.series_by_nationality?.length ? (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={14}>
              <Table<SeriesNationalityStatItem>
                dataSource={stats.series_by_nationality}
                rowKey="nationality_name"
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="small"
                columns={[
                  {
                    title: "Kraj",
                    dataIndex: "nationality_name",
                    key: "nationality_name",
                    render: (
                      name: string,
                      record: SeriesNationalityStatItem,
                    ) =>
                      name === "Brak kraju" ? (
                        <Text type="secondary">—</Text>
                      ) : (
                        <Space>
                          {record.nationality_flag_url && (
                            <img
                              src={record.nationality_flag_url}
                              alt={name}
                              style={{
                                width: 20,
                                height: 14,
                                borderRadius: 2,
                                display: "block",
                                objectFit: "cover",
                              }}
                            />
                          )}
                          {name}
                        </Space>
                      ),
                  },
                  {
                    title: "Seriale",
                    dataIndex: "series_count",
                    key: "series_count",
                    align: "center" as const,
                    render: (v: number) => <Text type="secondary">{v}</Text>,
                  },
                  {
                    title: "Rozpoczęte sezony",
                    dataIndex: "seasons_count",
                    key: "seasons_count",
                    align: "center" as const,
                    render: (v: number) => <strong>{v}</strong>,
                  },
                  {
                    title: "Czas oglądania",
                    dataIndex: "total_minutes",
                    key: "total_minutes",
                    align: "right" as const,
                    render: (v: number) => formatMinutes(v),
                  },
                ]}
                summary={(data) => {
                  const totSeries = data.reduce(
                    (s, r) => s + r.series_count,
                    0,
                  );
                  const totSeasons = data.reduce(
                    (s, r) => s + r.seasons_count,
                    0,
                  );
                  const totMinutes = data.reduce(
                    (s, r) => s + r.total_minutes,
                    0,
                  );
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <strong>Razem</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center">
                        <Text type="secondary">{totSeries}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="center">
                        <strong>{totSeasons}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <strong>{formatMinutes(totMinutes)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Col>
            <Col xs={24} lg={10}>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={seriesNationalityChartData.slice(0, 8)}
                    layout="vertical"
                    margin={{ left: 10, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="name"
                      type="category"
                      width={120}
                      tick={{ fontSize: 12, fill: "#595959" }}
                    />
                    <Tooltip formatter={(value) => [`${value} sezonów`, ""]} />
                    <Bar
                      dataKey="seasons"
                      fill={COLORS.series}
                      radius={[0, 4, 4, 0]}
                      barSize={20}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>
        ) : (
          <Empty description="Brak danych o krajach produkcji (seriale)" />
        )}
      </Card>

      <Divider style={{ margin: "32px 0" }} />

      {/* ============================================================= */}
      {/* 📚 KSIĄŻKI                                                     */}
      {/* ============================================================= */}
      <Title level={3} style={{ marginBottom: 16 }}>
        <BookOutlined style={{ marginRight: 8, color: COLORS.books }} />
        Książki
      </Title>

      {/* BOOKS BY PUBLISHER */}
      <Card
        title={
          <span>
            <BankOutlined style={{ marginRight: 8, color: COLORS.books }} />
            Książki wg wydawnictw
          </span>
        }
        loading={statsLoading}
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        {stats?.books_by_publisher?.length ? (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={14}>
              <Table<PublisherStatItem>
                dataSource={stats.books_by_publisher}
                rowKey="publisher_name"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Wydawnictwo",
                    dataIndex: "publisher_name",
                    key: "publisher_name",
                    render: (name: string) => (
                      <Tag
                        color="green"
                        style={{ fontSize: 13, padding: "2px 10px" }}
                      >
                        {name}
                      </Tag>
                    ),
                  },
                  {
                    title: "Liczba książek",
                    dataIndex: "books_count",
                    key: "books_count",
                    align: "center" as const,
                    render: (v: number) => <strong>{v}</strong>,
                  },
                  {
                    title: "Strony",
                    dataIndex: "total_pages",
                    key: "total_pages",
                    align: "right" as const,
                    render: (v: number) => v.toLocaleString("pl-PL"),
                  },
                ]}
                summary={(data) => {
                  const totBooks = data.reduce((s, r) => s + r.books_count, 0);
                  const totPages = data.reduce((s, r) => s + r.total_pages, 0);
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <strong>Razem</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center">
                        <strong>{totBooks}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <strong>{totPages.toLocaleString("pl-PL")}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Col>
            <Col xs={24} lg={10}>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={stats.books_by_publisher.slice(0, 8)}
                    layout="vertical"
                    margin={{ left: 10, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="publisher_name"
                      type="category"
                      width={120}
                      tick={{ fontSize: 12, fill: "#595959" }}
                    />
                    <Tooltip formatter={(value) => [`${value} książek`, ""]} />
                    <Bar
                      dataKey="books_count"
                      fill={COLORS.books}
                      radius={[0, 4, 4, 0]}
                      barSize={20}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>
        ) : (
          <Empty description="Brak danych o książkach" />
        )}
      </Card>

      {/* ============================================================= */}
      {/* BOOK PURCHASE COSTS                                           */}
      {/* ============================================================= */}
      <Card
        title={
          <span>
            <DollarOutlined
              style={{ marginRight: 8, color: COLORS.books }}
            />
            Koszty zakupu książek
          </span>
        }
        loading={statsLoading}
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        {stats?.book_purchase_costs?.items?.length ? (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={14}>
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={8}>
                  <Statistic
                    title="Łączny koszt"
                    value={stats.book_purchase_costs.total_cost}
                    precision={2}
                    suffix="zł"
                    valueStyle={{ color: COLORS.books, fontWeight: 600 }}
                  />
                </Col>
                <Col xs={8}>
                  <Statistic
                    title="Liczba książek"
                    value={stats.book_purchase_costs.books_count}
                    valueStyle={{ fontWeight: 600 }}
                  />
                </Col>
                <Col xs={8}>
                  <Statistic
                    title="Średnia cena"
                    value={stats.book_purchase_costs.avg_price}
                    precision={2}
                    suffix="zł"
                    valueStyle={{ fontWeight: 600 }}
                  />
                </Col>
              </Row>
              <Table<BookPurchaseCostItem>
                dataSource={stats.book_purchase_costs.items}
                rowKey={(r) => `${r.title}-${r.finished_at}`}
                pagination={false}
                size="small"
                columns={[
                  {
                    title: "Tytuł",
                    dataIndex: "title",
                    key: "title",
                    render: (title: string) => (
                      <Tag
                        color="green"
                        style={{ fontSize: 13, padding: "2px 10px" }}
                      >
                        {title}
                      </Tag>
                    ),
                  },
                  {
                    title: "Cena",
                    dataIndex: "price",
                    key: "price",
                    align: "right" as const,
                    render: (v: number) => (
                      <strong>{formatMoney(v)}</strong>
                    ),
                  },
                  {
                    title: "Data ukończenia",
                    dataIndex: "finished_at",
                    key: "finished_at",
                    align: "right" as const,
                    render: (d: string | null) =>
                      d ? new Date(d).toLocaleDateString("pl-PL") : <Text type="secondary">—</Text>,
                  },
                ]}
                summary={(data) => {
                  const totCost = data.reduce((s, r) => s + r.price, 0);
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <strong>Razem ({data.length} książek)</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <strong>{formatMoney(totCost)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}></Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </Col>
            <Col xs={24} lg={10}>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={stats.book_purchase_costs.items.slice(0, 8)}
                    layout="vertical"
                    margin={{ left: 10, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickFormatter={(v) => `${v} zł`} />
                    <YAxis
                      dataKey="title"
                      type="category"
                      width={140}
                      tick={{ fontSize: 11, fill: "#595959" }}
                    />
                    <Tooltip
                      formatter={(value) => [
                        formatMoney(Number(value ?? 0)),
                        "Cena",
                      ]}
                    />
                    <Bar
                      dataKey="price"
                      fill={COLORS.books}
                      radius={[0, 4, 4, 0]}
                      barSize={20}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>
        ) : (
          <Empty description="Brak danych o kosztach zakupu książek" />
        )}
      </Card>

      <Divider style={{ margin: "32px 0" }} />

      {/* ============================================================= */}
      {/* ⭐ WSZYSTKIE DZIEŁA                                            */}
      {/* ============================================================= */}
      <Title level={3} style={{ marginBottom: 16 }}>
        <StarOutlined style={{ marginRight: 8, color: "#faad14" }} />
        Wszystkie dzieła
      </Title>

      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {/* Rating distribution chart */}
        <Col xs={24} lg={12}>
          <Card
            title="Rozkład ocen"
            style={{ borderRadius: 12, height: "100%" }}
            loading={statsLoading}
          >
            {ratingChartData.length ? (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={ratingChartData}
                    margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f0f0f0"
                    />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#595959" }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: "#8c8c8c" }}
                    />
                    <Tooltip
                      formatter={(value) => [`${value} dzieł`, "Ilość"]}
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={32}>
                      {ratingChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Empty description="Brak ocen" />
            )}
          </Card>
        </Col>

        {/* Average ratings per type */}
        <Col xs={24} lg={12}>
          <Card
            title="Średnie oceny wg typu"
            style={{ borderRadius: 12, height: "100%" }}
            loading={statsLoading}
          >
            {stats?.rating_stats?.averages &&
              Object.keys(stats.rating_stats.averages).length > 0 ? (
              <Row gutter={[16, 16]}>
                {Object.entries(stats.rating_stats.averages).map(
                  ([type, avg]) => (
                    <Col xs={24} sm={8} key={type}>
                      <Card
                        size="small"
                        style={{
                          borderRadius: 12,
                          textAlign: "center",
                          background:
                            type === "book"
                              ? "#f6ffed"
                              : type === "movie"
                                ? "#e6f7ff"
                                : "#fff7e6",
                          border:
                            type === "book"
                              ? "1px solid #b7eb8f"
                              : type === "movie"
                                ? "1px solid #91d5ff"
                                : "1px solid #ffd591",
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          {TYPE_ICONS[type]}
                          <Text strong style={{ marginLeft: 6 }}>
                            {TYPE_LABELS[type] || type}
                          </Text>
                        </div>
                        <Statistic
                          value={avg.avg_rating}
                          precision={2}
                          suffix="/ 8"
                          valueStyle={{ fontSize: 24, fontWeight: 700 }}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ({avg.rated_count} ocen)
                        </Text>
                      </Card>
                    </Col>
                  ),
                )}
              </Row>
            ) : (
              <Empty description="Brak ocen" />
            )}

            {/* Overall distribution bars */}
            {ratingChartData.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <Text strong style={{ marginBottom: 12, display: "block" }}>
                  Procentowy rozkład
                </Text>
                {(() => {
                  const total = ratingChartData.reduce(
                    (a, b) => a + b.count,
                    0,
                  );
                  return ratingChartData.map((r) => (
                    <div
                      key={r.rating}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 8,
                      }}
                    >
                      <Text
                        style={{ width: 120, fontSize: 13, textAlign: "right" }}
                      >
                        {r.label}
                      </Text>
                      <Progress
                        percent={
                          total > 0 ? Math.round((r.count / total) * 100) : 0
                        }
                        strokeColor={r.fill}
                        size="small"
                        style={{ flex: 1 }}
                        format={(p) => `${p}% (${r.count})`}
                      />
                    </div>
                  ));
                })()}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Top rated works */}
      {stats?.rating_stats?.top_rated?.length ? (
        <Card
          title={
            <span>
              <TrophyOutlined style={{ marginRight: 8, color: "#faad14" }} />
              Najwyżej ocenione ({filterTitle})
            </span>
          }
          loading={statsLoading}
          style={{ marginBottom: 24, borderRadius: 12 }}
        >
          <Table
            dataSource={stats.rating_stats.top_rated}
            rowKey={(r, i) => `${r.title}-${i}`}
            pagination={false}
            size="small"
            columns={[
              {
                title: "#",
                key: "index",
                width: 50,
                render: (_: unknown, __: unknown, index: number) => (
                  <Text
                    strong
                    style={{ color: index < 3 ? "#faad14" : "#8c8c8c" }}
                  >
                    {index + 1}
                  </Text>
                ),
              },
              {
                title: "Tytuł",
                dataIndex: "title",
                key: "title",
                render: (title: string) => <Text strong>{title}</Text>,
              },
              {
                title: "Typ",
                dataIndex: "work_type",
                key: "work_type",
                render: (type: string) => (
                  <Tag
                    color={
                      type === "book"
                        ? "green"
                        : type === "movie"
                          ? "blue"
                          : "gold"
                    }
                  >
                    {TYPE_LABELS[type] || type}
                  </Tag>
                ),
              },
              {
                title: "Ocena",
                dataIndex: "rating",
                key: "rating",
                align: "center" as const,
                render: (rating: number) => <RatingDisplay value={rating} />,
              },
              {
                title: "Data ukończenia",
                dataIndex: "finished_at",
                key: "finished_at",
                render: (d: string | null) =>
                  d ? new Date(d).toLocaleDateString("pl-PL") : "—",
              },
            ]}
          />
        </Card>
      ) : null}

      {/* Top rated by type */}
      {stats?.rating_stats?.top_rated_by_type ? (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {(["movie", "series", "book"] as const).map((wtype) => {
            const items = stats.rating_stats.top_rated_by_type[wtype];
            if (!items?.length) return null;
            const typeColor =
              wtype === "book"
                ? COLORS.books
                : wtype === "movie"
                  ? COLORS.movies
                  : COLORS.series;
            const typeIcon =
              wtype === "movie" ? (
                <ShopOutlined style={{ marginRight: 8, color: typeColor }} />
              ) : wtype === "book" ? (
                <BankOutlined style={{ marginRight: 8, color: typeColor }} />
              ) : (
                <Tv size="1em" style={{ marginRight: 8, color: typeColor }} />
              );
            return (
              <Col xs={24} lg={8} key={wtype}>
                <Card
                  title={
                    <span style={{ fontSize: 14 }}>
                      {typeIcon}
                      Top {TYPE_LABELS[wtype] || wtype}
                    </span>
                  }
                  size="small"
                  style={{ borderRadius: 12, height: "100%" }}
                >
                  <Table
                    dataSource={items}
                    rowKey={(r, i) => `${r.title}-${i}`}
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: "#",
                        key: "index",
                        width: 36,
                        render: (_: unknown, __: unknown, index: number) => (
                          <Text
                            strong
                            style={{ color: index < 3 ? "#faad14" : "#8c8c8c" }}
                          >
                            {index + 1}
                          </Text>
                        ),
                      },
                      {
                        title: "Tytuł",
                        dataIndex: "title",
                        key: "title",
                        ellipsis: true,
                        render: (title: string) => <Text strong>{title}</Text>,
                      },
                      {
                        title: "Ocena",
                        dataIndex: "rating",
                        key: "rating",
                        width: 120,
                        align: "center" as const,
                        render: (rating: number) => (
                          <RatingDisplay value={rating} />
                        ),
                      },
                    ]}
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : null}

      <Divider style={{ margin: "32px 0" }} />

      {/* ============================================================= */}
      {/* GENRE BREAKDOWN                                               */}
      {/* ============================================================= */}
      <Card
        title={
          <span>
            <PieChartOutlined style={{ marginRight: 8, color: COLORS.total }} />
            Gatunki ({filterTitle})
          </span>
        }
        loading={statsLoading}
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        {genreChartData.length ? (
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <div style={{ width: "100%", height: 340 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={genreChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      innerRadius={50}
                      label={({ name, value }) => `${name} (${value})`}
                      labelLine={true}
                    >
                      {genreChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              {stats?.genre_breakdown?.map((g, i) => {
                const maxCount = Math.max(
                  ...(stats.genre_breakdown?.map((x) => x.works_count) ?? [1]),
                );
                return (
                  <div
                    key={g.genre_name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 10,
                    }}
                  >
                    <Text
                      style={{
                        width: 160,
                        fontSize: 13,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {g.genre_name}
                    </Text>
                    <Progress
                      percent={Math.round((g.works_count / maxCount) * 100)}
                      strokeColor={PIE_COLORS[i % PIE_COLORS.length]}
                      size="small"
                      style={{ flex: 1 }}
                      format={() => `${g.works_count}`}
                    />
                  </div>
                );
              })}
            </Col>
          </Row>
        ) : (
          <Empty description="Brak danych o gatunkach" />
        )}
      </Card>
    </div>
  );
}

export default MyLibraryStatsPage;
